import type { PackageLine, ShipmentSummary } from '@freight-cbm/shared-types';

export interface ExportRow {
  length: string;
  width: string;
  height: string;
  weight: string;
  quantity: string;
  dimUnit: string;
  weightUnit: string;
  cbm: string;
  dimWeight: string;
  chargeableWeight: string;
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function packageLinesToExportRows(
  lines: PackageLine[],
  summary: ShipmentSummary
): ExportRow[] {
  return lines.map((pkg, idx) => {
    const lineResult = summary.lineResults[idx];
    return {
      length: String(pkg.length),
      width: String(pkg.width),
      height: String(pkg.height),
      weight: String(pkg.weight),
      quantity: String(pkg.quantity),
      dimUnit: pkg.dimUnit,
      weightUnit: pkg.weightUnit,
      cbm: lineResult ? lineResult.cbm.toFixed(6) : '0',
      dimWeight: lineResult ? lineResult.dimWeight.toFixed(2) : '0',
      chargeableWeight: lineResult ? lineResult.chargeableWeight.toFixed(2) : '0',
    };
  });
}

export function exportToCSV(rows: ExportRow[]): string {
  const headers = [
    'length', 'width', 'height', 'weight', 'quantity',
    'dimUnit', 'weightUnit', 'cbm', 'dimWeight', 'chargeableWeight',
  ];

  const headerLine = headers.join(',');
  const dataLines = rows.map((row) =>
    headers.map((h) => escapeCSV(row[h as keyof ExportRow])).join(',')
  );

  return [headerLine, ...dataLines].join('\n');
}

export function downloadCSV(csvContent: string, filename: string = 'cbm-calculation.csv'): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function generateAndDownloadCSV(
  packages: PackageLine[],
  summary: ShipmentSummary
): void {
  const rows = packageLinesToExportRows(packages, summary);
  const csv = exportToCSV(rows);
  const timestamp = new Date().toISOString().slice(0, 10);
  downloadCSV(csv, `cbm-calculation-${timestamp}.csv`);
}
